/**
 * Mexican craft malthouses — Central Altiplano (Maltería Mexicana Altiplano),
 * Puebla.
 *
 * One house so far. Central Altiplano publish full technical sheets, so these
 * rows are `spec-sheet`, and every row passes `origin: 'Mexico'` to the shared
 * craft helper.
 */

import type { Fermentable } from '../../../schema/fermentable';
import { craftMalt } from './helpers';

/** Mexican craft malts. @see Fermentable */
export const mexicoCraftFermentables: readonly Fermentable[] = [
  // ---------------------------------------------------------------------------
  // Central Altiplano (Maltería Mexicana Altiplano) — Puebla, Mexico
  //
  // Mexico's first independent malthouse, opened in 2018 at the former
  // Hacienda Virreyes in Oriental, Puebla; sales and the registered address are
  // in Santa Isabel Cholula. They also resell Boortmalt and Viking malts, and
  // only the products whose sheets say "elaborada a partir de cebada/trigo
  // mexicana dentro de la Ex-Hacienda Virreyes" are here — six malts and a
  // roasted barley, matching the "6 variedades de malta nacional" on their home
  // page.
  //
  // Every figure is from their 2025 technical sheets, translated from Spanish
  // and published on calidadaltiplano.net, their quality site. Extract is a
  // minimum and moisture a maximum, stored as published; colour is printed in
  // both EBC and °L, and the EBC band's mid-point is passed through with °L
  // converted from it; diastatic power is a °WK minimum converted at
  // `(WK + 16) / 3.5`. The crystal, chocolate and roasted barley sheets print no
  // enzyme figure, and zero is what the process leaves.
  //
  // The 2023 commercial sheets agree with these. No `url`: the sheets live at a
  // 300-plus-character upload path, and the store's product pages now 404.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'central-altiplano-pilsen-2h',
    name: 'Pilsen 2H',
    maker: 'Central Altiplano',
    origin: 'Mexico',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Mexican two-row pilsner malt with a sweet, artisan-bread flavor and a touch of honey, pale beige in the sack. Built for lagers but usable as the whole grist of any style.',
    tags: ['base', 'pilsner', 'lager', 'two-row', 'craft-malt', 'mexico', 'puebla'],
  }),
  craftMalt({
    id: 'central-altiplano-pale-ale-2h',
    name: 'Pale Ale 2H',
    maker: 'Central Altiplano',
    origin: 'Mexico',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.4,
    colorEbc: 5.25,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Their flagship Mexican two-row: pale gold, with caramel-and-bread sweetness and a nutty, biscuit aroma. An ale base that also carries stouts, porters, IPAs and lagers at up to 100 percent.',
    tags: ['base', 'pale', 'ale', 'two-row', 'craft-malt', 'mexico', 'puebla'],
  }),
  craftMalt({
    id: 'central-altiplano-chit',
    name: 'Chit',
    maker: 'Central Altiplano',
    origin: 'Mexico',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 2.8,
    colorEbc: 6.25,
    diastaticPowerLintner: 47,
    suggestedMaxPercent: 15,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'A deliberately under-modified Mexican barley malt that keeps more protein, for body and foam stability in any style. Pale, cereal-sweet and crisp; use it at 5–15 percent alongside a base malt.',
    tags: ['base', 'chit', 'head-retention', 'body', 'craft-malt', 'mexico', 'puebla'],
  }),
  craftMalt({
    id: 'central-altiplano-trigo',
    name: 'Trigo',
    maker: 'Central Altiplano',
    origin: 'Mexico',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.3,
    colorEbc: 4.9,
    diastaticPowerLintner: 129,
    suggestedMaxPercent: 60,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Mexican wheat malt with a whole-grain bread aroma and a light sweetness, for witbier, weissbier and any beer that wants more body and steadier foam.',
    tags: ['base', 'wheat', 'head-retention', 'craft-malt', 'mexico', 'puebla'],
  }),
  craftMalt({
    id: 'central-altiplano-caramelo-60',
    name: 'Caramelo 60',
    maker: 'Central Altiplano',
    origin: 'Mexico',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 6,
    colorLovibond: 60.3,
    colorEbc: 158.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    provenance: 'spec-sheet',
    description:
      'A 60 °L caramel malt from Mexican two-row, amber to light copper, with caramel sweetness and a subtle toast, for bitters, IPAs, porters and stouts.',
    tags: ['crystal', 'caramel', 'medium', 'craft-malt', 'mexico', 'puebla'],
  }),
  craftMalt({
    id: 'central-altiplano-chocolate',
    name: 'Chocolate',
    maker: 'Central Altiplano',
    origin: 'Mexico',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    provenance: 'spec-sheet',
    description:
      'A dark roasted malt with bitter chocolate, coffee and a caramel edge and an intense cocoa aroma, for porters, stouts and schwarzbier. A small dose adjusts color and roast in a Vienna lager or bock.',
    tags: ['roasted', 'chocolate', 'coffee', 'craft-malt', 'mexico', 'puebla'],
  }),
  craftMalt({
    id: 'central-altiplano-cebada-tostada',
    name: 'Cebada Tostada',
    maker: 'Central Altiplano',
    origin: 'Mexico',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    provenance: 'spec-sheet',
    description:
      'Roasted unmalted Mexican barley: pronounced roast with a lightly smoky edge and hints of caramel and coffee. The dry roast of a stout or porter, a few percent at a time.',
    tags: ['roasted', 'roasted-barley', 'unmalted', 'stout', 'craft-malt', 'mexico', 'puebla'],
  }),
];
